import KaboodleToaster from "components/common/KaboodleToaster";
import type { FieldErrors } from "react-hook-form";

import type { FormData } from "../shared/IntakeTypes";

/*
each array is a group of fields that are validated together, representing a page of the form
*/
export const fieldValidationMap = [
  // DocumentUploadAndAdjusterIntake (combined first page). For Liberty
  // Mutual/Helmsman submitters this page also carries the Liberty referral
  // fields card, required to match the backend (ClientIntakeFileSerializer).
  // These 5 names are only ever registered in form state when the Liberty
  // card is actually rendered (isLibertyUser), so triggering them for a
  // non-Liberty submitter is a harmless no-op -- there's nothing registered
  // to validate.
  [
    "documents",
    "additional_ack_email_recipients",
    "grs_or_legacy_rcg",
    "rcg_carrier",
    "segmentation_type",
    "referral_type",
    "wcrc_case_id",
  ],

  // ClaimantIntakeForm - all fields are optional
  [],

  // EmployerIntakeForm (includes Employer + Insurer + TPA)
  [
    "employerName",
    "employerAddress",
    "employerState",
    "employerZipCode",
    "insurerName",
    "insurerAddress",
    "insurerState",
    "insurerZipCode",
    "tpaName",
    "tpaAddress",
    "tpaState",
    "tpaZipCode",
  ],

  // AccidentInfoIntakeForm - handled by custom logic
  [],
];

// map of field names to friendly names for error messages
const friendlyFieldNames: Record<string, string> = {
  wcmsa_zip_code: "WCMSA Zip Code",
  one_eleven_jurisdiction: "111 Jurisdiction",
  accident_description: "Accident Description",
  description_of_claimants_job: "Description of Claimant's Job",
  accepted_conditions: "Accepted Conditions",
  denied_conditions: "Denied Conditions",
  jurisdiction: "WCMSA State",
  documents: "Documents",
  additional_ack_email_recipients: "Acknowledgement Emails",
  services: "Services",
  due_date: "Due Date",
  adjusterName: "Adjuster Name",
  adjusterEmail: "Adjuster Email",
  adjusterStreet: "Adjuster Street Address",
  adjusterState: "Adjuster State",
  adjusterZipCode: "Adjuster Zip Code",
  adjusterPhone: "Adjuster Phone",
  claimantFirstName: "Claimant First Name",
  claimantLastName: "Claimant Last Name",
  claimantGender: "Claimant Gender",
  claimantSSN: "Claimant SSN",
  claimantAddress: "Claimant Address",
  claimantCity: "Claimant City",
  claimantState: "Claimant State",
  claimantZipCode: "Claimant Zip Code",
  claimantMedicareID: "Claimant Medicare ID",
  claimantBirthDate: "Claimant Birth Date",
  insuranceType: "Insurance Types",
  employerName: "Employer Name",
  employerAddress: "Employer Address",
  employerState: "Employer State",
  employerZipCode: "Employer Zip Code",
  insurerName: "Insurer Name",
  insurerAddress: "Insurer Address",
  insurerState: "Insurer State",
  insurerZipCode: "Insurer Zip Code",
  tpaName: "TPA Name",
  tpaAddress: "TPA Address",
  tpaState: "TPA State",
  tpaZipCode: "TPA Zip Code",
  grs_or_legacy_rcg: "GRS or Legacy RCG File",
  segmentation_type: "Segmentation Type",
  rcg_carrier: "RCG Carrier",
  referral_type: "Referral Type",
  wcrc_case_id: "Case ID Number (MSA)",
};

export const useIntakeValidation = () => {
  // on final submission, show any errors or required fields that are missing
  const handleSubmitError = (errors: FieldErrors<FormData>) => {
    const errorCount = Object.keys(errors).length;

    // create user-friendly field names for errors
    const errorFieldNames: string[] = [];
    Object.keys(errors).forEach((fieldName) => {
      const friendlyName = friendlyFieldNames[fieldName] || fieldName;
      errorFieldNames.push(friendlyName);
    });

    // create error message
    const message =
      errorCount === 1
        ? `Please complete the required field: ${errorFieldNames[0]}`
        : `Please complete the following required fields:\n• ${errorFieldNames.join("\n• ")}`;

    KaboodleToaster.error(message);
  };

  // on Next navigation, show any errors or required fields that are missing
  const showFieldValidationErrors = (
    fieldsToValidate: (keyof FormData)[],
    errors: FieldErrors<FormData>,
  ) => {
    const errorFields: string[] = [];

    // add error fields to array
    fieldsToValidate.forEach((fieldName) => {
      if (errors[fieldName]) {
        const friendlyName = friendlyFieldNames[fieldName] || fieldName;
        errorFields.push(friendlyName);
      }
    });

    // create error message
    if (errorFields.length > 0) {
      const message =
        errorFields.length === 1
          ? `Please complete the required field: ${errorFields[0]}`
          : `Please complete the following required fields:\n• ${errorFields.join("\n• ")}`;

      KaboodleToaster.error(message);
    } else {
      KaboodleToaster.error(
        "Please complete all required fields before continuing.",
      );
    }
  };

  return {
    handleSubmitError,
    showFieldValidationErrors,
  };
};
